import type { MarketingFrame, MarketingLayout, SectionId } from "../types";
import { SECTION_IDS } from "../types";
import { SECTION_SCORE, sampleStage, type StageSample } from "../score";

/*
 * Закрепление блока.
 *
 * Раньше каждая анимация шла от прогресса секции, а он начинается за экран до
 * того, как блок появится, и заканчивается, когда блок уже ушёл. Из-за этого
 * человек видел только середину чужого движения.
 *
 * Потом появилось удержание — но ход анимации всё ещё занимал его целиком,
 * вместе со въездом и затуханием. Замер показал, во что это выливается:
 * в блоке «Бери простые фриланс-заказы» первый узел был виден 825px, а
 * последний — 50px. Закон был монотонный: чем позже такт, тем меньше он виден.
 *
 * Теперь время блока живёт в партитуре (`../score`). Здесь только перевод
 * прокрутки в её координаты. Ни одного тайминга в этом файле нет и быть не
 * должно — если тянет добавить число, ему место в партитуре.
 */

/** Блоки, которые встают в кадр и держатся, пока идёт их собственная анимация. */
export const PINNED_SECTIONS = SECTION_IDS.filter(
  (id) => SECTION_SCORE[id].pin && SECTION_SCORE[id].hold > 0,
) as readonly SectionId[];

export type PinnedSectionId = SectionId;

const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));

export interface BlockPin {
  /** Насколько сместить содержимое вверх-вниз, чтобы кадр стоял неподвижно. */
  readonly offsetPx: number;
  /** Ход самой анимации блока: 0 — кадр только встал, 1 — анимация досмотрена. */
  readonly progress: number;
  /** Блок сейчас в кадре и удерживается. */
  readonly held: boolean;
  /** Блок где-то в кадре: въезжает, стоит или уезжает. Он виден и не гасится. */
  readonly onStage: boolean;
  /** Фаза прохождения блока через кадр. */
  readonly phase: StageSample["phase"];
}

/**
 * Удержание — всё, что длиннее одного экрана. Длину задаёт партитура; здесь
 * она только читается из уже измеренной раскладки.
 */
export function resolveHoldPx(layout: MarketingLayout, id: SectionId): number {
  const metric = layout.sections[id];
  if (!metric) return 0;
  if (!SECTION_SCORE[id]?.pin) return 0;
  return Math.max(0, metric.endPx - metric.startPx - layout.viewportHeight);
}

export function sampleBlockPin(
  frame: MarketingFrame,
  layout: MarketingLayout,
  id: SectionId,
): BlockPin {
  const metric = layout.sections[id];
  if (!metric) return { offsetPx: 0, progress: 0, held: false, onStage: false, phase: "before" };
  const spanPx = Math.max(1, metric.endPx - metric.startPx);
  const travelled = frame.travelPx - metric.startPx;
  const sample = sampleStage(id, travelled, spanPx, layout.viewportHeight);
  return {
    offsetPx: sample.offsetPx,
    progress: clamp01(sample.stage),
    held: sample.readable,
    onStage: sample.onStage,
    phase: sample.phase,
  };
}

/**
 * Кадр, в котором у каждого блока прогресс — это ход его собственной анимации,
 * а не положение секции в документе. Всё, что рисует блоки, читает именно его,
 * поэтому начало и конец движения гарантированно попадают на экран.
 */
export function withPinnedProgress(frame: MarketingFrame, layout: MarketingLayout | null): MarketingFrame {
  if (!layout) return frame;
  /*
   * На телефоне блок никуда не едет: раздел — это один неподвижный экран,
   * который переключается целиком. Подмена прогресса рассчитана на проход
   * блока через кадр и для секции высотой ровно в экран даёт неподвижные 0,5
   * при любом положении. Замерено на подвале: его финал застревал на половине
   * и подвал оставался невидимым — в кадре стояли одни объекты финала.
   */
  if (frame.viewport === "mobile") return frame;
  const sections = { ...frame.sections };
  for (const id of SECTION_IDS) {
    if (!layout.sections[id]) continue;
    /* Блокам с собственным движком удержания прогресс не подменяем: у них своя
       раскладка тактов, и подмена ломает её. Проверено на «Создавай 3D-объекты»:
       после подмены четвёртый и пятый такт переставали появляться вовсе. */
    if (!SECTION_SCORE[id].pin) continue;
    sections[id] = { progress: sampleBlockPin(frame, layout, id).progress };
  }
  return { ...frame, sections };
}
